import React, { useMemo, useState } from 'react'
import {
  useModalContext,
  Button,
  Space,
  TextHeading,
  TextBody,
  ButtonGroup,
  Alert,
  Box,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import {
  Form,
  Forms,
  useFormsContext,
  RadioButton,
  RadioGroup,
} from '@ally/forms'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import {
  Beneficiary,
  DeleteBeneEntry,
  DeleteNonIraBenePayload,
} from '../../../types'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import { useNonIraBeneficiaries } from '../../../services/NonIraBeneficiariesProvider'
import { FundsAllocationFields } from '../../common/FundsAllocationFields'
import { removeLeadingZeros } from '../../../utils/formatter'
import { fireErrorTagging, KeyValuePairObj } from '../../../utils/tagging'

type DeleteBeneFormValues = Record<string, string | number>

export const DeleteBeneForm: React.FC<{
  remainingBeneficiaries?: Beneficiary[]
  isLoading: boolean
  formError: boolean
}> = ({ remainingBeneficiaries, isLoading, formError }) => {
  const { formId, values, setFieldValue, handleSubmit, errors, setFormError } =
    useFormsContext()
  const { setOpen } = useModalContext()

  const total = Object.keys(values)
    .filter(key => key !== 'fundAllocationType')
    .reduce((acc, curr) => {
      if (Number.isNaN(Number(values[curr]))) {
        return acc
      }
      return acc + Number(values[curr])
    }, 0)

  const handlePercentageChange = (name: string, value: string): void => {
    setFieldValue(name, value)
  }

  const onSubmit = (): void => {
    if (total !== 100) {
      fireErrorTagging({
        beneficiaryAllocation: 'Your total allocation must add up to 100%',
      } as KeyValuePairObj)
    }
    fireErrorTagging(errors as KeyValuePairObj)
    if (values.fundAllocationType !== 'specific' || total === 100) {
      setFormError('')
      handleSubmit()
    }
  }

  return (
    <Form id={formId}>
      <Space mb="md">
        <TextHeading
          tag="h1"
          size="sm"
          weight="bold"
          tabIndex={-1}
          focusOnMount
        >
          How do you want to reallocate your money?
        </TextHeading>
        <>
          {formError && (
            <Box mb="md">
              <Alert
                variant="error"
                disableContainer
                body="It's us - we're having a moment. We couldn't delete your beneficiary. We'll be quick about it, so try again in a bit."
                focusOnMount
              />
            </Box>
          )}
        </>
        <RadioGroup name="fundAllocationType" legendText="" isLegendHidden>
          <RadioButton
            label="Equally"
            value="equal"
            data-testid="equal-allocation"
          />
          <RadioButton
            label="Specific percentage"
            value="specific"
            data-testid="specific-allocation"
          />
        </RadioGroup>
        {values.fundAllocationType === 'specific' && (
          <FundsAllocationFields
            existingBeneficiaries={remainingBeneficiaries}
            total={total}
            setPercentageValue={handlePercentageChange}
            hideNewBeneAllocationInput
          />
        )}
        <ButtonGroup>
          <Button
            allytmln="Submit Reallocations Non IRA Beneficiary"
            text="Save"
            variant="primary"
            data-testid="save-allocation-button"
            loading={isLoading}
            onClick={onSubmit}
            // disabled={values.fundAllocationType === 'specific' && total !== 100}
          />
          <Button
            allytmln="Cancel Delete Non IRA Beneficiary"
            text="Cancel"
            variant="link"
            data-testid="save-cancel-button"
            onClick={(): void => {
              setOpen(false)
            }}
          />
        </ButtonGroup>
      </Space>
    </Form>
  )
}

export const NonIraDeleteModalContent: React.FC<{
  beneficiaryId: string
  accountId: string
  beneficiaries?: Beneficiary[]
}> = ({ beneficiaryId, accountId, beneficiaries }) => {
  const { setOpen } = useModalContext()
  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState(false)
  const [reallocateStep, setReallocateStep] = useState(false)
  const { deleteBeneficiary } = useBeneficiariesApi()
  const { getBeneficiaries } = useNonIraBeneficiaries()
  const { triggerSnackbar } = useTriggerSnackbar()

  const beneficiariesRemainingLength = useMemo(() => {
    if (beneficiaries && beneficiaries.length > 0) {
      return beneficiaries.length - 1
    }
    return -1
  }, [beneficiaries])

  const beneficiary = useMemo(() => {
    return (
      beneficiaries &&
      beneficiaries.find(bene => bene.beneficiaryId === beneficiaryId)
    )
  }, [beneficiaryId, beneficiaries])

  const buildAutoAllocatedPayload = (
    values?: DeleteBeneFormValues,
  ): DeleteNonIraBenePayload => {
    if (values && values.fundAllocationType === 'equal') {
      return {
        accountId,
        beneficiaries: [
          {
            beneficiaryId,
            isDelete: true,
            allocationPercentage: 'Equal',
          },
        ],
      }
    }
    // istanbul ignore next - unnecessary null check, beneficiaries have to exist
    const updatedBeneficiaries = beneficiaries?.map(bene => {
      const isCurrent = bene.beneficiaryId === beneficiaryId
      const entry: DeleteBeneEntry = {
        beneficiaryId: bene.beneficiaryId,
        isDelete: isCurrent,
      }
      if (!isCurrent)
        entry.allocationPercentage =
          values && values.fundAllocationType
            ? removeLeadingZeros(values[bene.beneficiaryId])
            : '100'
      return entry
    })
    return { accountId, beneficiaries: updatedBeneficiaries }
  }

  const callDeleteBeneficiary = async (
    values?: DeleteBeneFormValues,
  ): Promise<void> => {
    try {
      setIsLoading(true)
      await deleteBeneficiary(buildAutoAllocatedPayload(values))
      await getBeneficiaries()
      setIsLoading(false)
      // istanbul ignore next // we have tested bene being deleted, but can't test snackbar
      triggerSnackbar({
        text: 'You removed a beneficiary from your account and we adjusted your funds allocation. Please review these changes.',
        columnSize: 4,
      })
      // istanbul ignore next - tagging
      allytm.event('customEvent', {
        name: 'formSuccess',
        type: 'formValidationSuccess',
        attr: {
          formValues: {
            form: 'Delete Non-IRA Beneficiary Reallocation Form',
            values: [],
          },
        },
      })
    } catch (e) {
      setFormError(true)
      setIsLoading(false)
      allytm.event(
        'customError',
        'It’s us – we’re having a moment. We couldn’t delete your beneficiary. We’ll be quick about it, so try again in a bit.',
        '',
      )
    }
  }

  const deleteBeneficiaryClick = (): void => {
    if (beneficiariesRemainingLength > 1) {
      // manual reallocation
      setReallocateStep(true)
    } else {
      // automatic reallocation
      callDeleteBeneficiary()
    }
  }

  if (!beneficiary || beneficiariesRemainingLength === -1) return <></>

  // istanbul ignore next - optional chaining
  const remainingBeneficiaries = beneficiaries?.filter(
    bene => bene.beneficiaryId !== beneficiaryId,
  )

  const initialValues: DeleteBeneFormValues =
    remainingBeneficiaries && remainingBeneficiaries.length
      ? remainingBeneficiaries.reduce(
          (acc, curr) => {
            return {
              ...acc,
              [curr.beneficiaryId]:
                curr.allocationPercentage &&
                curr.allocationPercentage !== 'Equal'
                  ? parseInt(curr.allocationPercentage, 10).toString()
                  : '0',
            }
          },
          {
            fundAllocationType:
              beneficiary.allocationPercentage === 'Equal'
                ? 'equal'
                : 'specific',
          },
        )
      : {}

  const handleSubmit = (values: DeleteBeneFormValues): void => {
    // if (values.fundAllocationType === 'specific' && total !== 100)
    callDeleteBeneficiary(values)
  }

  if (reallocateStep)
    return (
      <Forms
        formId="delete-beneficiary-form"
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <DeleteBeneForm
          remainingBeneficiaries={remainingBeneficiaries}
          isLoading={isLoading}
          formError={formError}
        />
      </Forms>
    )

  return (
    <Space mb="md">
      <TextHeading tag="h1" size="sm" weight="bold" tabIndex={-1}>
        Remove beneficiary?
      </TextHeading>
      <>
        {formError && (
          <Box mb="md">
            <Alert
              variant="error"
              disableContainer
              body="It's us - we're having a moment. We couldn't delete your beneficiary. We'll be quick about it, so try again in a bit."
            />
          </Box>
        )}
      </>
      {beneficiariesRemainingLength === 0 && (
        <TextBody tag="p" size="sm" weight="normal">
          If you remove{' '}
          <Private as="span">
            {beneficiary.firstName ?? beneficiary.beneficiaryName}
          </Private>
          , you&apos;ll have no beneficiaries linked to your account.
        </TextBody>
      )}
      {beneficiariesRemainingLength === 1 && (
        <TextBody tag="p" size="sm" weight="normal">
          If you remove{' '}
          <Private as="span">
            {beneficiary.firstName ?? beneficiary.beneficiaryName}
          </Private>
          , we&apos;ll reallocate your money to your other beneficiary.
        </TextBody>
      )}
      {beneficiariesRemainingLength > 1 && (
        <TextBody tag="p" size="sm" weight="normal">
          If you remove{' '}
          <Private as="span">
            {beneficiary.firstName ?? beneficiary.beneficiaryName}
          </Private>
          , you&apos;ll need to reallocate your money among your other
          beneficiaries.
        </TextBody>
      )}
      <ButtonGroup>
        <Button
          allytmln="Submit Delete Non IRA Beneficiary"
          text="Remove"
          variant="destructive"
          data-testid="delete-confirm-button"
          onClick={deleteBeneficiaryClick}
          loading={isLoading}
        />
        <Button
          allytmln="Cancel Delete Non IRA Beneficiary"
          text="Cancel"
          variant="link"
          data-testid="delete-cancel-button"
          onClick={(): void => {
            setOpen(false)
          }}
        />
      </ButtonGroup>
    </Space>
  )
}
